import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { programmeService } from '@/lib/api/services/programme'
import { programmeCommentService, type Comment } from '@/lib/api/services/programme-comment'
import type { ProgrammeFilterParams, PaginationParams, CreateProgramme, UpdateProgramme, ApiResponse, PaginatedResponse, ProgrammeStats, ProgrammeStatsResponse } from '@/lib/api/types'

// Query keys for programme management
export const programmeKeys = {
  all: ['programme'] as const,
  programmes: () => [...programmeKeys.all, 'programmes'] as const,
  programme: (id: string) => [...programmeKeys.programmes(), id] as const,
  programmesPaginated: (params: PaginationParams, filters: ProgrammeFilterParams) =>
    [...programmeKeys.programmes(), 'paginated', params, filters] as const,
  programmesSearch: (query: string, params: PaginationParams) => [...programmeKeys.programmes(), 'search', query, params] as const,
  stats: () => [...programmeKeys.all, 'stats'] as const,
  comments: (programmeId: string) => [...programmeKeys.all, 'comments', programmeId] as const,
}


export const useProgrammes = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: ProgrammeFilterParams = {}
) => {
  return useQuery({
    queryKey: programmeKeys.programmesPaginated(params, filters),
    queryFn: () => programmeService.getProgrammes(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData
  })
}


export const useProgramme = (programmeId: string) => {
  return useQuery({
    queryKey: programmeKeys.programme(programmeId),
    queryFn: () => programmeService.getProgrammeById(programmeId),
    enabled: !!programmeId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}


export const useProgrammeStats = () => {
  return useQuery<ProgrammeStatsResponse, Error, ProgrammeStats>({
    queryKey: programmeKeys.stats(),
    queryFn: () => programmeService.getProgrammeStats(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData
  })
}


export const useCreateProgramme = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateProgramme) => programmeService.createProgramme(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programmeKeys.programmes() })
      queryClient.invalidateQueries({ queryKey: programmeKeys.stats() })
    },
    onError: (error) => {
      console.error('Programme creation failed:', error)
    }
  })
}


export const useUpdateProgramme = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ programmeId, data }: { programmeId: string; data: UpdateProgramme }) =>
      programmeService.updateProgramme(programmeId, data),
    onSuccess: (_response, { programmeId }) => {
      queryClient.invalidateQueries({ queryKey: programmeKeys.programme(programmeId) })
      queryClient.invalidateQueries({ queryKey: programmeKeys.programmes() })
      queryClient.invalidateQueries({ queryKey: programmeKeys.stats() })
    },
    onError: (error) => {
      console.error('Programme update failed:', error)
    }
  })
}


export const useUpdateProgrammeStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ programmeId, status }: { programmeId: string; status: 'DRAFT' | 'PUBLISH' }) =>
      programmeService.updateProgrammeStatus(programmeId, status),
    onSuccess: (response, { programmeId }) => {
      if (response.success && response.data) {
        queryClient.setQueryData(programmeKeys.programme(programmeId), response.data)
      }

      queryClient.invalidateQueries({ queryKey: programmeKeys.programmes() })
      queryClient.invalidateQueries({ queryKey: programmeKeys.stats() })
    },
    onError: (error) => {
      console.error('Programme status update failed:', error)
    }
  })
}


export const useDeleteProgramme = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (programmeId: string) => programmeService.deleteProgramme(programmeId),
    onSuccess: (_, programmeId) => {
      queryClient.removeQueries({ queryKey: programmeKeys.programme(programmeId) })
      queryClient.invalidateQueries({ queryKey: programmeKeys.programmes() })
      queryClient.invalidateQueries({ queryKey: programmeKeys.stats() })
    },
    onError: (error) => {
      console.error('Programme deletion failed:', error)
    }
  })
}

export const useSearchProgrammes = (
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: programmeKeys.programmesSearch(searchBy || '', params),
    queryFn: () => programmeService.searchProgrammes(params, searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook for infinite scroll programmes
export const useInfiniteProgrammes = (baseFilters: ProgrammeFilterParams = {}) => {
  return useInfiniteQuery({
    queryKey: [...programmeKeys.programmes(), 'infinite', baseFilters],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      programmeService.getProgrammes({ page: pageParam, size: 10 }, baseFilters),
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages: any[]) => {
      if (!lastPage.success || !lastPage.data) return undefined
      const currentPage = allPages.length
      const totalPages = lastPage.data.totalPage
      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    staleTime: 2 * 60 * 1000
  })
}

/**
 * Hook for infinite scroll programme comments
 */
export const useInfiniteProgrammeComments = (programmeId: string) => {
  return useInfiniteQuery({
    queryKey: programmeKeys.comments(programmeId || ''),
    queryFn: () =>
      programmeCommentService.getComments(programmeId),
    enabled: !!programmeId,
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<PaginatedResponse<Comment>>, allPages: ApiResponse<PaginatedResponse<Comment>>[]) => {
      if (!lastPage.success || !lastPage.data) return undefined
      const currentPage = allPages.length
      const totalPages = lastPage.data.totalPage
      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    staleTime: 1 * 60 * 1000 // 1 minute
  })
}

/**
 * Hook to fetch programme highlights
 */
export const useProgrammeHighlights = (type: string = 'HOME_PAGE_AFSA_PROGRAMME') => {
  return useQuery({
    queryKey: [...programmeKeys.all, 'highlights', type],
    queryFn: () => programmeService.getProgrammeHighlights(type),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to update programme highlights
 */
export const useUpdateProgrammeHighlights = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (programmeIds: string[]) =>
      programmeService.updateProgrammeHighlights(programmeIds),
    onSuccess: () => {
      // Invalidate highlights query to refetch
      queryClient.invalidateQueries({ queryKey: [...programmeKeys.all, 'highlights'] })
    },
    onError: (error) => {
      console.error('Failed to update highlights:', error)
    }
  })
}
